import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flag, Loader2, X } from "lucide-react";
import toast from "../../Toast/toast";
import { API } from "../../../service/Interceptor";

const REASONS = [
  "Spam or misleading",
  "Harassment or hate",
  "Inappropriate content",
  "Impersonation",
  "Copyright or IP issue",
  "Other",
];

const field =
  "w-full rounded-xl border border-zinc-800 bg-zinc-900/60 px-3.5 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:border-[#6C63F5]/50 transition-colors";

const ReportModal = ({
  id,
  reportModal,
  setReportModal,
  type = "Post",
  coverImage: _coverImage = true,
  handleSubmit,
  setReport,
}) => {
  const [selected, setSelected] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);

  const label = type?.toLowerCase() || "post";

  const buildReason = () => {
    const note = details.trim();
    if (!selected) return note;
    if (selected === "Other") return note;
    return note ? `${selected}. ${note}` : selected;
  };

  const syncReport = (reason, note) => {
    if (!setReport) return;
    const next =
      !reason
        ? note.trim()
        : reason === "Other"
          ? note.trim()
          : note.trim()
            ? `${reason}. ${note.trim()}`
            : reason;
    setReport(next);
  };

  useEffect(() => {
    if (!reportModal) {
      setSelected("");
      setDetails("");
      setBusy(false);
    }
  }, [reportModal]);

  const close = () => {
    setReportModal(false);
    setSelected("");
    setDetails("");
    setReport?.("");
  };

  const canSubmit =
    selected === "Other" ? details.trim().length > 0 : !!selected;

  const handleReport = async () => {
    const reason = buildReason();
    if (!reason) {
      toast.error("Please select a reason");
      return;
    }

    if (handleSubmit) {
      setReport?.(reason);
      handleSubmit(reason);
      return;
    }

    setBusy(true);
    const payload = { reason };
    try {
      if (type === "Post") {
        const res = await API.post(`api/post/${id}/report`, payload);
        if (res.data.success) {
          toast.success("Post has been reported");
          close();
        }
      } else if (type === "Project") {
        const res = await API.post(`api/jobs/${id}/report`, payload);
        if (res.data.success) {
          toast.success("Project has been reported");
          close();
        }
      }
    } catch (error) {
      console.log(error);
      toast.error("Could not submit report. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {reportModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={close}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-modal-title"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl shadow-black/40"
          >
            <div className="flex items-start justify-between gap-3 mb-5">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-xl bg-amber-400/10 border border-amber-400/20 flex items-center justify-center shrink-0">
                  <Flag size={18} className="text-amber-300" />
                </div>
                <div className="min-w-0">
                  <h3
                    id="report-modal-title"
                    className="font-display font-bold text-xl tracking-tight text-zinc-50"
                  >
                    Report this {label}
                  </h3>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    Help keep the community safe — reports are reviewed by our
                    team.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={close}
                className="text-zinc-500 hover:text-zinc-200 transition-colors shrink-0"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-5">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500 mb-2.5">
                  Why are you reporting?
                </div>
                <div className="flex flex-wrap gap-2">
                  {REASONS.map((reason) => {
                    const active = selected === reason;
                    return (
                      <button
                        key={reason}
                        type="button"
                        onClick={() => {
                          setSelected(reason);
                          syncReport(reason, details);
                        }}
                        className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                          active
                            ? "border-amber-400/50 bg-amber-400/15 text-amber-200"
                            : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                        }`}
                      >
                        {reason}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label
                  htmlFor="report-details"
                  className="block text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500 mb-2"
                >
                  {selected === "Other"
                    ? "Describe the issue"
                    : "Add details (optional)"}
                </label>
                <textarea
                  id="report-details"
                  rows={4}
                  value={details}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDetails(next);
                    syncReport(selected, next);
                  }}
                  placeholder={
                    selected === "Other"
                      ? "Tell us what happened…"
                      : "Any extra context that helps us review this…"
                  }
                  className={`${field} resize-none`}
                />
              </div>

              <div className="flex items-center gap-2.5 pt-1">
                <button
                  type="button"
                  onClick={handleReport}
                  disabled={busy || !canSubmit}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#6C63F5] px-5 py-3 text-sm font-semibold text-white hover:bg-[#5751E5] transition-colors disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Flag size={15} />
                  )}
                  Submit report
                </button>
                <button
                  type="button"
                  onClick={close}
                  disabled={busy}
                  className="rounded-full border border-zinc-800 px-5 py-3 text-sm font-medium text-zinc-300 hover:border-zinc-700 hover:text-zinc-100 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportModal;
